import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_RIDE_INVOICE_SETTINGS, RIDE_INVOICE_PLACEHOLDERS, fillRideInvoiceTemplate, type RideInvoiceSettings } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';

const SAMPLE = {
  Kunde: 'Erika Mustermann',
  Rechnungsnummer: 'RE-2026-0042',
  Betrag: '95,00 €',
  Auftrag: 'AU-2026-0017',
  Fahrt: '22.10.2026, Flughafen München → München Innenstadt',
  Firma: 'Ihr Fahrdienst',
  Beglichen: '',
};
const SAMPLE_SETTLED = {
  ...SAMPLE,
  Betrag: '0,00 €',
  Beglichen: 'Der Rechnungsbetrag beträgt 0,00 €: Ihre Fahrt wurde vollständig durch Gutschein, Aktionscode oder Rabatt beglichen. Es ist nichts zu zahlen. Die Rechnungskopie im Anhang zeigt die Angaben dazu.',
};

/**
 * Einrichtung → Rechnung nach der Fahrt (KON-247): Schließt der Fahrer eine Fahrt in der App ab, erstellt Wirkia die Rechnung unter Ausgangsrechnungen und schickt sie dem Kunden. Hier schaltet der
 * Mandant die Automatik ein oder aus und passt den Text der Mail an. Die Rechnung entsteht bewusst erst nach dem Fahrtende, weil bis dahin noch etwas dazukommen kann (andere Route, mehr Kilometer, Wartezeit).
 */
export function RideInvoicePage() {
  const client = useQueryClient();
  const { can } = useSession();
  const canWrite = can('setup.write');
  const settings = useQuery({ queryKey: ['ride-invoice-settings'], queryFn: async () => (await api<{ data: RideInvoiceSettings }>('GET', '/ride-invoice-settings')).data });
  const [form, setForm] = useState<RideInvoiceSettings>(DEFAULT_RIDE_INVOICE_SETTINGS);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [showSettled, setShowSettled] = useState(false);
  const body = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: async () => (await api<{ data: RideInvoiceSettings }>('PUT', '/ride-invoice-settings', form)).data,
    onSuccess: (saved) => {
      client.setQueryData(['ride-invoice-settings'], saved);
      setFields({});
      setMessage({ ok: true, text: 'Gespeichert.' });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setFields(e.fields);
        setMessage({ ok: false, text: 'Bitte prüfen Sie Ihre Eingaben.' });
      } else {
        setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Das Speichern hat nicht geklappt.' });
      }
    },
  });

  /** Fügt den Platzhalter an der Stelle des Cursors im Text ein */
  const insert = (key: string) => {
    const el = body.current;
    const token = `{{${key}}}`;
    if (!el) {
      setForm((f) => ({ ...f, body: `${f.body}${token}` }));
      return;
    }
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? start;
    const next = `${form.body.slice(0, start)}${token}${form.body.slice(end)}`;
    setForm((f) => ({ ...f, body: next }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  if (settings.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!settings.data) return <div className="alert alert--error" role="alert">Die Einstellungen konnten nicht geladen werden.</div>;
  const values = showSettled ? SAMPLE_SETTLED : SAMPLE;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    save.mutate();
  };

  return (
    <form onSubmit={submit} noValidate>
      <section className="card">
        <h2>Rechnung nach der Fahrt</h2>
        <p className="muted small">
          Schließt der Fahrer eine Fahrt in Wirkia Drivers ab, erstellt Wirkia daraus die Rechnung unter Ausgangsrechnungen, stellt sie aus und schickt sie dem Kunden als PDF. Das gilt auch für Fahrten, deren Betrag
          ein Gutschein oder Aktionscode vollständig deckt: Die Rechnung über 0,00 € wird ebenfalls erstellt und aufbewahrt, der Kunde bekommt die Kopie mit dem Vermerk, wie sie beglichen wurde.
        </p>
        <p className="muted small">
          Eine Fahrt ist schon mit „Fahrgast angetroffen“ zahlungspflichtig. Die Rechnung entsteht trotzdem erst nach dem Fahrtende, weil bis dahin noch etwas dazukommen kann: eine geänderte Route, mehr Kilometer, ein
          Zwischenstopp mit Wartezeit oder ein Unfall. Der Betrag stammt aus dem Auftrag, den das Büro bis dahin noch anpassen kann.
        </p>
        <label className="check">
          <input type="checkbox" checked={form.auto} disabled={!canWrite} onChange={(e) => setForm({ ...form, auto: e.target.checked })} />
          <span>Rechnung nach Abschluss der Fahrt automatisch erstellen und an den Kunden senden</span>
        </label>
        {!form.auto && <div className="alert alert--warn" role="status">Ausgeschaltet: Abgeschlossene Fahrten bleiben „Erledigt“, die Rechnung erstellen Sie selbst unter Ausgangsrechnungen.</div>}
      </section>

      <section className="card">
        <h2>Text der Mail an den Kunden</h2>
        <p className="muted small">Die Rechnung hängt als PDF an. Mit den Platzhaltern setzt Wirkia die Angaben der Fahrt ein, Sie fügen sie mit einem Klick an der Stelle des Cursors ein.</p>
        <label className="field">
          <span>Betreff</span>
          <input className="input" value={form.subject} disabled={!canWrite} maxLength={200} onChange={(e) => setForm({ ...form, subject: e.target.value })} aria-invalid={!!fields.subject} />
          {fields.subject && <small className="field__error" role="alert">{fields.subject}</small>}
        </label>
        <label className="field">
          <span>Text</span>
          <textarea ref={body} className="input" rows={12} value={form.body} disabled={!canWrite} maxLength={5000} onChange={(e) => setForm({ ...form, body: e.target.value })} aria-invalid={!!fields.body} />
          {fields.body && <small className="field__error" role="alert">{fields.body}</small>}
        </label>
        {canWrite && (
          <div className="row-actions" aria-label="Platzhalter einfügen">
            {RIDE_INVOICE_PLACEHOLDERS.map((p) => (
              <button key={p.key} type="button" className="btn btn--secondary btn--sm" title={p.label} onClick={() => insert(p.key)}>{`{{${p.key}}}`}</button>
            ))}
          </div>
        )}
        <ul className="muted small">
          {RIDE_INVOICE_PLACEHOLDERS.map((p) => <li key={p.key}><code>{`{{${p.key}}}`}</code>: {p.label}</li>)}
        </ul>
      </section>

      <section className="card">
        <h2>Vorschau</h2>
        <label className="check">
          <input type="checkbox" checked={showSettled} onChange={(e) => setShowSettled(e.target.checked)} />
          <span>Beispiel einer Rechnung über 0,00 € (durch Gutschein beglichen)</span>
        </label>
        <p><strong>{fillRideInvoiceTemplate(form.subject, values)}</strong></p>
        <pre className="code-block" style={{ whiteSpace: 'pre-wrap' }}>{fillRideInvoiceTemplate(form.body, values)}</pre>
      </section>

      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {canWrite && (
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
          <button className="btn btn--secondary" type="button" onClick={() => setForm({ ...form, subject: DEFAULT_RIDE_INVOICE_SETTINGS.subject, body: DEFAULT_RIDE_INVOICE_SETTINGS.body })}>Text zurücksetzen</button>
        </div>
      )}
    </form>
  );
}
